import { Link } from 'react-router';
import { CreditCard, TriangleAlert } from 'lucide-react';
import type { ClubDetail } from '@repo/shared-types';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { EmptyState } from '@repo/ui/components/empty-state';
import { canSeeSubscription } from '@/lib/club-subscription';
import { urlConfig, urlControlPanel, urlHome } from '@/urls';

/**
 * In place of a screen the API answered 402. Without the club's detail (a
 * layout that doesn't load it) the role is unknown, so it sends to the control
 * panel, which knows it.
 */
export function SubscriptionExpired({ club, clubId }: { club?: ClubDetail; clubId: string }) {
  if (!club) {
    return (
      <EmptyState
        icon={CreditCard}
        title="La suscripción del club venció"
        description="Mientras no se renueve, la gestión del club está bloqueada."
        actions={
          <Button asChild>
            <Link to={urlControlPanel(clubId)}>Ir al club</Link>
          </Button>
        }
      />
    );
  }
  if (canSeeSubscription(club)) {
    return (
      <EmptyState
        icon={CreditCard}
        title="La suscripción del club venció"
        description="Renuévala para volver a usar la gestión del club. Tus torneos públicos y tu perfil en playpadel.io siguen visibles."
        actions={
          <Button asChild>
            <Link to={urlConfig(club.id, 'suscripcion')}>Renovar suscripción</Link>
          </Button>
        }
      />
    );
  }
  return (
    <EmptyState
      icon={CreditCard}
      title="La suscripción del club venció"
      description="Pídele al dueño del club que la renueve para volver a usar la gestión del club."
      actions={
        <Button variant="outline" asChild>
          <Link to={urlHome()}>Ver mis clubes</Link>
        </Button>
      }
    />
  );
}

/** The last charge failed and the club is living on its days of grace. */
export function GraceBanner({ club }: { club: ClubDetail }) {
  if (!club.subscription.inGrace) return null;
  const isOwner = canSeeSubscription(club);
  return (
    <Alert variant="warning" className="mb-4">
      <TriangleAlert />
      <AlertTitle>No pudimos cobrar la suscripción del club</AlertTitle>
      <AlertDescription>
        {isOwner
          ? 'Renuévala en los próximos días para no perder el acceso a la gestión del club.'
          : 'Pídele al dueño del club que la renueve en los próximos días para no perder el acceso.'}
      </AlertDescription>
      {isOwner && (
        <AlertAction>
          <Button size="sm" variant="outline" asChild>
            <Link to={urlConfig(club.id, 'suscripcion')}>Ver suscripción</Link>
          </Button>
        </AlertAction>
      )}
    </Alert>
  );
}
